import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { AddressFields } from '../types';
import { searchAddressByZipcode, formatPostalCode } from '../utils/zipcloud';

const PREFECTURES = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

// 必須バリデーション対象フィールド（building は任意なので除外）
type RequiredAddressField = 'postal_code' | 'prefecture' | 'city' | 'street';

const REQUIRED_FIELD_LABELS: Record<RequiredAddressField, string> = {
  postal_code: '郵便番号',
  prefecture: '都道府県',
  city: '市区町村',
  street: '番地',
};

interface AddressInputProps {
  value: AddressFields;
  onChange: (address: AddressFields) => void;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  // 親が登録ボタン押下などのタイミングでバリデーションを発動させるためのフラグ
  showErrors?: boolean;
}

const AddressInput: React.FC<AddressInputProps> = ({
  value,
  onChange,
  required = false,
  disabled = false,
  size = 'small',
  showErrors = false,
}) => {
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // required指定時かつ親がエラー表示を有効化した場合のみ、空フィールドを未入力エラーとする
  // （初期表示時・blur時はエラーを出さず、登録ボタン押下後にまとめて表示する）
  const isRequiredEmpty = (field: RequiredAddressField): boolean => {
    if (!required || !showErrors) return false;
    return !value[field] || !String(value[field]).trim();
  };

  const requiredErrorText = (field: RequiredAddressField): string =>
    `${REQUIRED_FIELD_LABELS[field]}は必須です`;

  const handleFieldChange = useCallback(
    (field: keyof AddressFields, fieldValue: string) => {
      onChange({ ...value, [field]: fieldValue || undefined });
    },
    [value, onChange]
  );

  const doSearch = useCallback(
    async (zipcode: string) => {
      setSearching(true);
      setSearchError(null);
      const result = await searchAddressByZipcode(zipcode);
      setSearching(false);
      if (result) {
        onChange({
          ...value,
          postal_code: formatPostalCode(zipcode),
          prefecture: result.prefecture,
          city: result.city,
        });
      } else {
        setSearchError('住所が見つかりませんでした');
      }
    },
    [value, onChange]
  );

  const handlePostalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const formatted = formatPostalCode(raw);
      onChange({ ...value, postal_code: formatted || undefined });

      const digits = raw.replace(/[^\d]/g, '');
      if (digits.length === 7) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(digits), 300);
      } else {
        setSearchError(null);
      }
    },
    [value, onChange, doSearch]
  );

  const handleSearchClick = useCallback(() => {
    if (value.postal_code) {
      doSearch(value.postal_code);
    }
  }, [value.postal_code, doSearch]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label="郵便番号"
          placeholder="123-4567"
          value={value.postal_code || ''}
          onChange={handlePostalCodeChange}
          disabled={disabled}
          size={size}
          required={required}
          inputProps={{ maxLength: 8 }}
          sx={{ width: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">〒</InputAdornment>
            ),
          }}
          error={!!searchError || isRequiredEmpty('postal_code')}
          helperText={
            searchError
              ? searchError
              : isRequiredEmpty('postal_code')
                ? requiredErrorText('postal_code')
                : '7桁入力で自動検索'
          }
        />
        <Button
          variant="outlined"
          onClick={handleSearchClick}
          disabled={disabled || searching || !value.postal_code}
          size={size}
          sx={{ minWidth: 'auto', height: 40 }}
          startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
        >
          検索
        </Button>
      </Box>

      <TextField
        select
        label="都道府県"
        value={value.prefecture || ''}
        onChange={(e) => handleFieldChange('prefecture', e.target.value)}
        disabled={disabled}
        size={size}
        required={required}
        fullWidth
        error={isRequiredEmpty('prefecture')}
        helperText={isRequiredEmpty('prefecture') ? requiredErrorText('prefecture') : ''}
      >
        <MenuItem value="">
          <em>選択してください</em>
        </MenuItem>
        {PREFECTURES.map((pref) => (
          <MenuItem key={pref} value={pref}>
            {pref}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="市区町村"
        placeholder="渋谷区恵比寿"
        value={value.city || ''}
        onChange={(e) => handleFieldChange('city', e.target.value)}
        disabled={disabled}
        size={size}
        required={required}
        fullWidth
        error={isRequiredEmpty('city')}
        helperText={isRequiredEmpty('city') ? requiredErrorText('city') : ''}
      />

      <TextField
        label="番地"
        placeholder="1-1-1"
        value={value.street || ''}
        onChange={(e) => handleFieldChange('street', e.target.value)}
        disabled={disabled}
        size={size}
        required={required}
        fullWidth
        error={isRequiredEmpty('street')}
        helperText={isRequiredEmpty('street') ? requiredErrorText('street') : ''}
      />

      <TextField
        label="建物・部屋番号"
        placeholder="Re:crewマンション201"
        value={value.building || ''}
        onChange={(e) => handleFieldChange('building', e.target.value)}
        disabled={disabled}
        size={size}
        fullWidth
        helperText="任意"
      />
    </Box>
  );
};

export default AddressInput;
